import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { SetURLSearchParams } from 'react-router-dom';
import type { PropertyItem } from '../domain';
import { decodeHtml, buildPropertyUrl } from '../../../app/utils';

// OpenLayers
import 'ol/ol.css';
import './map-markers.css';
import OLMap from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import Overlay from 'ol/Overlay';
import { fromLonLat, toLonLat } from 'ol/proj';
import type { Extent } from 'ol/extent';

// ─── Colour helper ─────────────────────────────────────────────────────────────
//
// We read the ACTUAL computed background colour that DaisyUI applies to a live
// element with the given class.  This works with every DaisyUI version (v3 uses
// hsl(var(--p)), v4 uses oklch(var(--color-primary)), etc.) because we let the
// browser do the resolution rather than hard-coding a variable name.
//
function readComputedBg(className: string, fallback: string): string {
  const el = document.createElement('div');
  el.className = className;
  // Hidden but in the document so the browser can apply stylesheets
  el.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;pointer-events:none;';
  document.body.appendChild(el);
  const color = getComputedStyle(el).backgroundColor;
  document.body.removeChild(el);
  // getComputedStyle returns '' or 'rgba(0,0,0,0)' when no rule matched
  return color && color !== 'rgba(0, 0, 0, 0)' ? color : fallback;
}

// ─── Types ────────────────────────────────────────────────────────────────────

type ValidProperty = PropertyItem & { latitude: number; longitude: number };

interface MapViewProps {
  properties: PropertyItem[];
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
}

// ─── Popup ────────────────────────────────────────────────────────────────────

interface PopupProps {
  property: ValidProperty;
  searchParams: URLSearchParams;
  onClose: () => void;
}

function PropertyPopup({ property, searchParams, onClose }: PopupProps) {
  return (
    <div
      className="bg-white rounded-xl shadow-2xl border border-base-200 w-64"
      style={{ pointerEvents: 'auto' }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="relative">
        {property.coverImage && (
          <img
            src={property.coverImage}
            alt={property.titleLabel}
            className="w-full h-28 object-cover rounded-t-xl"
          />
        )}
        <button
          onClick={onClose}
          className="absolute top-1 right-1.5 bg-white/80 hover:bg-white rounded-full w-6 h-6 flex items-center justify-center text-base-content/70 hover:text-base-content font-bold text-sm shadow"
        >
          ×
        </button>
      </div>
      <div className="p-3">
        <h4 className="font-bold text-sm leading-tight mb-1 line-clamp-2">{property.titleLabel}</h4>
        <p className="text-xs text-base-content/60 mb-2">
          {property.houseTypeDescLabel} · {property.roomLabel} · {property.occupancyLabel}
        </p>
        <div className="text-primary font-bold text-base">{decodeHtml(property.perNightRateLabel)} / night</div>
        <div className="text-xs text-base-content/60 mt-0.5">{decodeHtml(property.totalPriceLabel)} total</div>
        <a
          href={buildPropertyUrl(property.identifier, searchParams)}
          className="btn btn-primary btn-sm w-full mt-2"
        >
          View Property
        </a>
      </div>
    </div>
  );
}

// ─── Helpers to colour a pin ──────────────────────────────────────────────────

function setPinColor(pinEl: HTMLDivElement, bg: string) {
  const bubble = pinEl.querySelector<HTMLDivElement>('.price-pin__bubble');
  const arrow = pinEl.querySelector<HTMLDivElement>('.price-pin__arrow');
  if (bubble) bubble.style.backgroundColor = bg;
  if (arrow) arrow.style.backgroundColor = bg;
}

// ─── MapView ──────────────────────────────────────────────────────────────────

export function MapView({ properties, searchParams, setSearchParams }: MapViewProps) {
  const mapDivRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<OLMap | null>(null);
  const popupOverlayRef = useRef<Overlay | null>(null);
  const markerOverlaysRef = useRef<Overlay[]>([]);
  const activePinRef = useRef<HTMLDivElement | null>(null);
  const isFirstMoveRef = useRef(true);

  // Resolved at mount time from actual DaisyUI computed styles
  const primaryBgRef = useRef<string>('#888');
  const activeBgRef = useRef<string>('#dc2626');

  const [popupEl, setPopupEl] = useState<HTMLDivElement | null>(null);
  const [selected, setSelected] = useState<ValidProperty | null>(null);
  const [showSearchHere, setShowSearchHere] = useState(false);

  const validProps = properties.filter(
    (p): p is ValidProperty => p.latitude != null && p.longitude != null,
  );

  // ── Initialize OL map once ────────────────────────────────────────────────
  useEffect(() => {
    if (!mapDivRef.current || mapRef.current) return;

    // Resolve the real DaisyUI primary colour now that the DOM + stylesheets are live
    primaryBgRef.current = readComputedBg('bg-primary', '#888');
    activeBgRef.current  = readComputedBg('bg-error',   '#dc2626');

    const first = validProps[0];
    const center = first ? fromLonLat([first.longitude, first.latitude]) : fromLonLat([0, 20]);

    const map = new OLMap({
      target: mapDivRef.current,
      layers: [new TileLayer({ source: new OSM() })],
      view: new View({ center, zoom: first ? 11 : 2 }),
    });

    // Restore prior boundary-search viewport if applicable
    const miln = searchParams.get('miln');
    const mila = searchParams.get('mila');
    const mxln = searchParams.get('mxln');
    const mxla = searchParams.get('mxla');
    if (miln && mila && mxln && mxla) {
      const sw = fromLonLat([parseFloat(miln), parseFloat(mila)]);
      const ne = fromLonLat([parseFloat(mxln), parseFloat(mxla)]);
      const extent: Extent = [sw[0], sw[1], ne[0], ne[1]];
      map.getView().fit(extent, { padding: [40, 40, 40, 40] });
    }

    // Popup overlay — OL positions this node; React writes content via portal.
    // offset [0, -50]: lift the popup above the price-pin bubble.
    const popupNode = document.createElement('div');
    const popupOverlay = new Overlay({
      element: popupNode,
      positioning: 'bottom-center',
      offset: [0, -50],
      stopEvent: true,
    });
    map.addOverlay(popupOverlay);
    popupOverlayRef.current = popupOverlay;
    setPopupEl(popupNode);

    map.on('click', () => closePopup());

    map.on('moveend', () => {
      if (isFirstMoveRef.current) {
        isFirstMoveRef.current = false;
        return;
      }
      setShowSearchHere(true);
    });

    mapRef.current = map;

    return () => {
      map.setTarget(undefined);
      mapRef.current = null;
      popupOverlayRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Sync price-pin overlays when properties change ────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    markerOverlaysRef.current.forEach((o) => map.removeOverlay(o));
    markerOverlaysRef.current = [];
    activePinRef.current = null;

    const newOverlays: Overlay[] = [];

    validProps.forEach((prop) => {
      // Structure:  .price-pin > .price-pin__bubble + .price-pin__arrow
      // Layout/transition come from map-markers.css.
      // Background colours are set inline so they work regardless of DaisyUI version.
      const pinEl = document.createElement('div');
      pinEl.className = 'price-pin';

      const bubbleEl = document.createElement('div');
      bubbleEl.className = 'price-pin__bubble';
      bubbleEl.textContent = decodeHtml(prop.perNightRateLabel);
      bubbleEl.style.backgroundColor = primaryBgRef.current;
      bubbleEl.style.color = 'white';

      const arrowEl = document.createElement('div');
      arrowEl.className = 'price-pin__arrow';
      arrowEl.style.backgroundColor = primaryBgRef.current;

      pinEl.appendChild(bubbleEl);
      pinEl.appendChild(arrowEl);

      pinEl.addEventListener('click', (e) => {
        e.stopPropagation();

        // Restore previous active pin to primary colour
        if (activePinRef.current && activePinRef.current !== pinEl) {
          setPinColor(activePinRef.current, primaryBgRef.current);
          activePinRef.current.classList.remove('active');
        }

        // Highlight this pin with the active (error) colour
        setPinColor(pinEl, activeBgRef.current);
        pinEl.classList.add('active');
        activePinRef.current = pinEl;

        const pos = fromLonLat([prop.longitude, prop.latitude]);
        setSelected(prop);
        popupOverlayRef.current?.setPosition(pos);
      });

      // 'bottom-center': bottom of .price-pin (= tip of arrow) is at the coordinate
      const overlay = new Overlay({
        element: pinEl,
        positioning: 'bottom-center',
        stopEvent: true,
      });
      overlay.setPosition(fromLonLat([prop.longitude, prop.latitude]));
      map.addOverlay(overlay);
      newOverlays.push(overlay);
    });

    markerOverlaysRef.current = newOverlays;

    return () => {
      newOverlays.forEach((o) => {
        try { map.removeOverlay(o); } catch { /* map may be disposed */ }
      });
    };
  }, [properties]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ──────────────────────────────────────────────────────────────

  function closePopup() {
    if (activePinRef.current) {
      setPinColor(activePinRef.current, primaryBgRef.current);
      activePinRef.current.classList.remove('active');
      activePinRef.current = null;
    }
    setSelected(null);
    popupOverlayRef.current?.setPosition(undefined);
  }

  function handleSearchHere() {
    const map = mapRef.current;
    if (!map) return;
    const size = map.getSize();
    if (!size) return;

    const extent = map.getView().calculateExtent(size);
    const [minLon, minLat] = toLonLat([extent[0], extent[1]]);
    const [maxLon, maxLat] = toLonLat([extent[2], extent[3]]);

    const next = new URLSearchParams(searchParams);
    next.set('bse', 'true');
    next.set('mxla', maxLat.toFixed(6));
    next.set('mxln', maxLon.toFixed(6));
    next.set('mila', minLat.toFixed(6));
    next.set('miln', minLon.toFixed(6));
    setSearchParams(next);
    setShowSearchHere(false);
    isFirstMoveRef.current = true;
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="relative w-full">
      <div
        ref={mapDivRef}
        style={{ width: '100%', height: '580px', borderRadius: '0.5rem' }}
      />

      {showSearchHere && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10" style={{ pointerEvents: 'none' }}>
          <button
            className="btn btn-primary shadow-xl"
            style={{ pointerEvents: 'auto' }}
            onClick={handleSearchHere}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            Search this area
          </button>
        </div>
      )}

      {popupEl &&
        createPortal(
          selected ? (
            <PropertyPopup
              property={selected}
              searchParams={searchParams}
              onClose={closePopup}
            />
          ) : null,
          popupEl,
        )}
    </div>
  );
}
